import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Pencil, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import {
  createOffice,
  lookupOwnerOffice,
  officeLabel,
  suggestOwnerOffice,
  type JstOfficeProposal,
  type JstOfficeSuggestion,
  type OfficeSourceBase,
} from "../../api";
import { useSelectableOffices, useTaskPolling } from "../../hooks";
import { OfficeFormModal } from "@/features/paper_send/pages/OfficesPage";
import { TaskLog } from "../ui";

export interface PickedOffice {
  office_id: string;
  full_name: string;
  street: string;
  building_no: string;
  postal_code: string;
  city: string;
}

/** Dla właściciela-instytucji z wypisu: propozycja rodzaju urzędu (gmina / starostwo /
 *  urząd marszałkowski / ZDW) + wybór z rejestru LUB dociągnięcie danych z internetu (AI)
 *  i utworzenie urzędu. Zwraca wybrany urząd, którym rodzic uzupełnia właściciela. */
export function OwnerOfficePickerModal({
  base, sourceId, ownerName, ownershipCode, teryt, parcelId, onPick, onClose,
}: {
  base: OfficeSourceBase;
  sourceId: string;
  ownerName: string;
  ownershipCode: string;
  teryt: string;
  parcelId: string | null;
  onPick: (o: PickedOffice) => void;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [office, setOffice] = useState("");
  const { data: offices } = useSelectableOffices(office);
  const [useDepartment, setUseDepartment] = useState(true);
  const [proposal, setProposal] = useState<JstOfficeProposal | null>(null);
  const [note, setNote] = useState("");
  const [taskId, setTaskId] = useState<string | null>(null);
  // Zły adres czy nieaktualny telefon widać właśnie w chwili wybierania urzędu — stąd
  // poprawka wprost tutaj, zamiast wybrania złego wpisu albo założenia kolejnego.
  const [editing, setEditing] = useState(false);

  const suggestion = useQuery({
    queryKey: ["pp", "owner-office-suggest", base, sourceId, ownerName, ownershipCode, parcelId],
    queryFn: () => suggestOwnerOffice(base, sourceId, {
      owner_name: ownerName, ownership_code: ownershipCode, teryt, parcel_id: parcelId,
    }),
  });
  const sug = suggestion.data;

  // Preselect the top registry match once the suggestion lands.
  useEffect(() => {
    if (sug?.existing?.length && !office) setOffice(sug.existing[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sug]);

  const finish = (o: {
    id: string; name: string; department?: string;
    street?: string; building_no?: string; postal_code?: string; city?: string;
  }) =>
    onPick({
      office_id: o.id,
      // Z komórką tylko na życzenie — czasem nie mamy pewności, że wydział/referat
      // jest właściwy, i wolimy ogólny adres urzędu.
      full_name: useDepartment && o.department ? `${o.name} — ${o.department}` : o.name,
      street: o.street ?? "",
      building_no: o.building_no ?? "",
      postal_code: o.postal_code ?? "",
      city: o.city ?? "",
    });

  const applyResult = (result: JstOfficeSuggestion) => {
    if (result.existing.length > 0) {
      setOffice(result.existing[0].id);
      setNote(`Znaleziono w rejestrze: ${officeLabel(result.existing[0])}`);
    } else if (result.proposal) {
      setProposal(result.proposal);
      setNote(result.proposal.via_web_search
        ? "Propozycja z internetu — zweryfikuj przed dodaniem."
        : "Propozycja z wiedzy modelu (bez internetu) — koniecznie zweryfikuj.");
    } else {
      setNote("Nie udało się znaleźć urzędu — wybierz z listy lub dodaj ręcznie w rejestrze urzędów.");
    }
  };

  const lookup = useMutation({
    mutationFn: () => lookupOwnerOffice(base, sourceId, {
      institution: sug!.institution!,
      commune: sug!.commune || undefined,
      county: sug!.county || undefined,
      voivodeship: sug!.voivodeship || undefined,
      teryts: [sug!.teryt].filter(Boolean),
      // Jednostka nazwana w wypisie — lookup ma szukać dokładnie jej.
      unit_name: sug!.unit_name || undefined,
    }),
    onSuccess: (result) => {
      setNote(""); setProposal(null);
      if (result.task_id) setTaskId(result.task_id);
      else applyResult(result);
    },
    onError: () => setNote("Wyszukiwanie nie powiodło się — wybierz z listy lub dodaj ręcznie."),
  });

  const task = useTaskPolling(taskId);
  useEffect(() => {
    if (!taskId || !task.data) return;
    const s = task.data.status;
    if (s === "success") {
      applyResult((task.data.result ?? { existing: [], proposal: null }) as JstOfficeSuggestion);
      setTaskId(null);
    } else if (["failure", "cancelled"].includes(s)) {
      setNote("Wyszukiwanie nie powiodło się — wybierz z listy lub dodaj ręcznie.");
      setTaskId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.data?.status]);
  const lookupRunning = lookup.isPending || !!taskId;

  const addOffice = useMutation({
    mutationFn: () => createOffice({
      name: proposal!.name, kind: proposal!.kind, department: proposal!.department,
      street: proposal!.street, building_no: proposal!.building_no,
      postal_code: proposal!.postal_code, city: proposal!.city,
      phone: proposal!.phone, email: proposal!.email,
      notes: [
        proposal!.website ? `WWW: ${proposal!.website}` : "",
        proposal!.source ? `Dodano z wyszukiwania AI (źródło: ${proposal!.source})` : "Dodano z wyszukiwania AI",
      ].filter(Boolean).join("\n"),
    }),
    onSuccess: (o) => {
      qc.invalidateQueries({ queryKey: ["pp", "offices"] });
      finish(o);
    },
    onError: () => setNote("Nie udało się dodać urzędu — uzupełnij brakujące pola adresu."),
  });

  const officeOptions = (() => {
    const matches = sug?.existing ?? [];
    const matchedIds = new Set(matches.map((m) => m.id));
    return [
      ...matches.map((m) => ({ value: m.id, label: `★ ${officeLabel(m)}` })),
      ...(offices ?? []).filter((o) => !matchedIds.has(o.id)).map((o) => ({ value: o.id, label: officeLabel(o) })),
    ];
  })();

  // Rekord wybrany w liście — z rejestru (pełny) albo z podpowiedzi dopasowania.
  const selectedOffice = (offices ?? []).find((x) => x.id === office);
  const pickSelected = () => {
    const o = selectedOffice ?? sug?.existing.find((x) => x.id === office);
    if (o) finish(o);
  };

  const setField = (k: keyof JstOfficeProposal, v: string) =>
    setProposal((p) => (p ? { ...p, [k]: v } : p));

  if (editing && selectedOffice) {
    return <OfficeFormModal office={selectedOffice} onClose={() => setEditing(false)} />;
  }

  return (
    <Modal open onClose={onClose} title="Dopasuj urząd do właściciela" size="lg">
      <div className="space-y-3 text-xs">
        <div className="text-[var(--color-text-secondary)]">
          Właściciel: <b>{ownerName || "—"}</b>
          {ownershipCode ? <> · grupa rejestrowa <span className="font-mono">{ownershipCode}</span></> : null}
        </div>

        {suggestion.isLoading ? (
          <div className="flex items-center gap-2 text-[var(--color-text-muted)]">
            <Sparkles size={13} className="animate-pulse" /> Dobieram rodzaj urzędu…
          </div>
        ) : (
          <div className="flex items-start gap-2 rounded border border-[var(--color-primary)]/40 bg-[var(--color-primary)]/5 px-2.5 py-2">
            <Sparkles size={14} className="mt-0.5 shrink-0 text-[var(--color-primary)]" />
            <span className="text-[var(--color-text-secondary)]">
              {sug?.reason}
              {sug?.institution && sug.existing.length === 0
                ? " Brak w rejestrze — pobierz dane z internetu lub wybierz z listy."
                : ""}
            </span>
          </div>
        )}

        <div className="flex items-center gap-1">
          <div className="flex-1">
            <SearchableSelect
              options={officeOptions}
              value={office}
              placeholder="Wybierz urząd z rejestru…"
              onChange={(e) => setOffice(e.target.value)}
            />
          </div>
          <Button size="sm" variant="secondary" disabled={!office} onClick={pickSelected}>
            Użyj
          </Button>
          <button
            type="button"
            className="p-1.5 border border-[var(--color-border-strong)] hover:bg-[var(--color-bg-elevated)] disabled:opacity-50"
            title="Popraw dane tego urzędu we wspólnym rejestrze (nazwa, adres, telefon, e-mail, ADE, konto)"
            disabled={!selectedOffice}
            onClick={() => setEditing(true)}
          >
            <Pencil size={13} />
          </button>
          <button
            type="button"
            className="p-1.5 border border-[var(--color-border-strong)] hover:bg-[var(--color-bg-elevated)] disabled:opacity-50"
            title="Pobierz dane urzędu z internetu (AI)"
            disabled={!sug?.institution || lookupRunning}
            onClick={() => { setNote(""); setProposal(null); lookup.mutate(); }}
          >
            <Sparkles size={13} className={lookupRunning ? "animate-pulse" : ""} />
          </button>
        </div>

        {(() => {
          const dept = selectedOffice?.department || "";
          return dept ? (
            <label className="flex items-center gap-2 text-[11px] text-[var(--color-text-secondary)] cursor-pointer">
              <input
                type="checkbox"
                checked={useDepartment}
                onChange={(e) => setUseDepartment(e.target.checked)}
              />
              Adresuj do komórki: {dept} — odznacz, jeśli wolisz ogólny adres urzędu
              (np. brak pewności, że to właściwy wydział)
            </label>
          ) : null;
        })()}

        {lookupRunning && (
          <div className="text-[11px] text-[var(--color-text-muted)] space-y-0.5">
            <div className="flex items-center gap-1"><Sparkles size={11} className="animate-pulse" /> Szukam danych urzędu…</div>
            <TaskLog log={task.data?.log ?? ""} />
          </div>
        )}
        {note && !lookupRunning && (
          <div className="text-[11px] text-[var(--color-text-secondary)]">{note}</div>
        )}

        {proposal && (
          <div className="border border-dashed border-[var(--color-border-strong)] p-3 space-y-2">
            <span className="section-label flex items-center gap-1">
              <Sparkles size={12} /> Proponowane dane urzędu — zweryfikuj i dodaj
            </span>
            {(proposal.contact_url || proposal.website || proposal.source) && (
              <a href={proposal.contact_url || proposal.website || proposal.source} target="_blank" rel="noreferrer"
                 title={proposal.contact_url || proposal.website || proposal.source}
                 className="inline-flex items-center gap-1 text-[11px] text-[var(--color-primary)] hover:underline">
                <ExternalLink size={11} /> Sprawdź adres i kontakt na stronie urzędu
              </a>
            )}
            <div className="grid grid-cols-2 gap-2">
              {([
                ["name", "Nazwa urzędu *"], ["department", "Wydział / komórka"],
                ["street", "Ulica *"], ["building_no", "Nr budynku *"],
                ["postal_code", "Kod pocztowy *"], ["city", "Miejscowość *"],
                ["phone", "Telefon"], ["email", "E-mail"],
              ] as [keyof JstOfficeProposal, string][]).map(([field, fieldLabel]) => (
                <label key={field} className="block">
                  <span className="section-label">{fieldLabel}</span>
                  <input className="input-base mt-0.5 text-xs" value={String(proposal[field] ?? "")}
                         onChange={(e) => setField(field, e.target.value)} />
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="secondary" onClick={() => setProposal(null)}>Odrzuć</Button>
              <Button size="sm" loading={addOffice.isPending}
                      disabled={!proposal.name || !proposal.street || !proposal.building_no || !proposal.postal_code || !proposal.city}
                      onClick={() => addOffice.mutate()}>
                Dodaj urząd i użyj
              </Button>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <Button variant="secondary" onClick={onClose}>Zamknij</Button>
        </div>
      </div>
    </Modal>
  );
}
